// matchGaps — WHO answers an open check, and WHAT control closes it.
//
// Pure and dependency-free (same contract as restrictionSentence.js): the Desk imports it,
// `node --test` exercises it, and nothing here reaches for a DOM, an i18n string or a fetch.
// It returns KEYS and FIELD NAMES; the panel turns those into Georgian and into writes.
//
// ── Why this exists at all (2026-08-08) ─────────────────────────────────────────────────
// The v2 matrix splits every `unknown` check two ways — amber "ask the owner" or violet
// "ask the client" — because that split is the only thing an agent DOES with an unknown:
// dial one person or the other. Until now that decision was made by
//
//     CLIENT_GAP_RE = /stated|requirement has no/   tested against `check.detail`
//
// which is a regex over the scorer's ENGLISH ENGINEER PROSE. It was right for the four
// checks it was written against (`no budget stated`, `requirement has no intent`, …) and
// silently wrong for everything added since. The one that matters:
//
//     detail: `owner restricted ${said}; the client profile does not say whether it applies`
//     delta:  { kind: 'household_gap', rule_id, actions: ['ask_client'] }
//
// That is the restriction-rules block (match-score v0.12.0, line ~1098) emitting the exact
// question the owner ruled must exist — *the owner forbids pets, this client never said
// whether they have one* — and declaring in `actions` that the CLIENT answers it. The prose
// contains neither "stated" nor "requirement has no", so the regex sent it to the OWNER:
// the strip asked the owner, again, a question only the client can answer, and the violet
// tier stayed empty on exactly the rows it was built for.
//
// 🔑 THE RULE: `delta.actions` is the scorer's own declaration of who is being asked. Read
// the declaration. The regex survives ONLY as the fallback for checks that carry no delta —
// and every one of those is a client gap by construction (`no budget stated`,
// `no location constraint stated`, `requirement has no intent/category`), which is why the
// fallback can stay a heuristic without being load-bearing.
//
// ⚠️ This is the same shape as the 2026-08-05 `cluster_restriction_evidence_v1` defect
// (B327) and the 2026-08-06 `scraped_by_party_id` one (B351): a surface re-deriving, from a
// downstream artefact, a fact the producer had already stated. Do not add a second regex
// here. If a check's audience is wrong, fix its `actions` in match-score.mjs.

// The scorer's action vocabulary, split by who is on the other end of the phone.
// `verify_owner` is an owner action even though the value is already recorded once — it is
// the certainty ladder asking for a second, better source, and that source is the owner.
const CLIENT_ACTIONS = new Set(['ask_client', 'confirm_flexibility', 'confirm_area_flexibility']);
const OWNER_ACTIONS = new Set(['ask_owner', 'verify_owner', 'verify_pin']);

// Kept from MatchSuggestions for checks with no delta at all. NOT exported: nothing should
// grow a second caller of it, and its whole justification is that the delta-less set is
// closed and client-side.
const NO_DELTA_CLIENT_RE = /stated|requirement has no/;

/**
 * 'client' | 'owner' — who can answer this open check.
 * Only meaningful for status:'unknown'; callers gate on that themselves.
 */
export function whoAnswers(check) {
  const actions = check?.delta?.actions;
  if (Array.isArray(actions)) {
    // Declared. A delta naming both audiences (none does today) resolves to the CLIENT,
    // because the client's own fact is always the cheaper call and gates the owner's.
    if (actions.some(a => CLIENT_ACTIONS.has(a))) return 'client';
    if (actions.some(a => OWNER_ACTIONS.has(a))) return 'owner';
  }
  // `household_gap` is the client's profile by definition — belt and braces, so a delta
  // that loses its actions in some future refactor still lands on the right side.
  if (check?.delta?.kind === 'household_gap') return 'client';
  return NO_DELTA_CLIENT_RE.test(check?.detail || '') ? 'client' : 'owner';
}

// ── `rule:<uuid>` check keys ─────────────────────────────────────────────────────────────
// The rules block keys its checks `rule:${rule.id || describePredicate(...)}` — a uuid on
// every real row. It rendered RAW in the Desk (screenshot 2026-08-08: two bands headed
// `rule:45d06c79-d21c-40df-8fab-135ff3a24d80`), because conditionLabel had no case for it
// and humanMatchKey's fallback is `key.replace(/_/g,' ')`. The rule's own sentence is what
// belongs there, and the panel already knows how to build it (restrictionSentence.js) from
// the rules it can fetch by listing id.
const RULE_KEY_RE = /^rule:(.+)$/;
export function ruleIdOf(key) {
  const m = RULE_KEY_RE.exec(String(key || ''));
  return m ? m[1] : null;
}

// ── Which criteria field answers which restriction AXIS ──────────────────────────────────
// A `household_gap` says "the owner restricted X and the client profile is silent". To offer
// a control, the panel needs the criteria field that would break the silence. The axes are
// restrictionSentence.js's AXIS_ORDER; the fields are execute_proposal's amend allowlist
// (EDITABLE_CRITERIA_FIELDS in MatchSuggestions.jsx) — both already exist, this is only the
// join between them.
//
// ⚠️ `codes` axes write into ONE array (`household_codes`) whose members are `pol_*` feature
// codes, and the panel cannot invent which code a nationality axis-value maps to. Those are
// therefore marked `editor: true`: the row says who answers and points at the demand editor
// rather than rendering a control that would guess. Honest beats clickable.
export const AXIS_FILL = {
  // The owner's example, and the one that started this: "no pets" + a client who never said.
  pet_species: { kind: 'bool', field: 'has_pets' },
  pet_size:    { kind: 'bool', field: 'has_pets' },
  // A count, not a yes/no — the same shape the owner side already uses for max_occupants.
  group_size:  { kind: 'number', field: 'occupants', min: 1, max: 30 },
  age_band:    { kind: 'bool', field: 'has_children' },
  nationality: { kind: 'codes', field: 'household_codes', editor: true },
  profession:  { kind: 'codes', field: 'household_codes', editor: true },
  sex:         { kind: 'codes', field: 'household_codes', editor: true },
  composition: { kind: 'codes', field: 'household_codes', editor: true },
  smoking:     { kind: 'codes', field: 'household_codes', editor: true },
  // 1191: no client field states it; the door is the demand editor, like the axes above.
  conduct:     { kind: 'codes', field: 'household_codes', editor: true },
  feature:     { kind: 'codes', field: 'household_codes', editor: true },
};

// ── Client-side fill by CHECK KEY (the non-rule gaps) ────────────────────────────────────
// 05-v2-answers Q2 fixes the priority: move_in_date → occupants → has_pets → has_children,
// with deposit and prepayment demoted to the quiet tier. `order` encodes exactly that, so
// the band sorts by the design's ruling rather than by whatever order the scorer emitted.
//
// `price` and `location` are deliberately NOT here. Budget is a number the client states,
// but a district is a multi-select and a polygon; both belong to the demand editor, and a
// one-tap control that could only ever set `price_max` would quietly overwrite a range.
export const CLIENT_FILL = {
  move_in:  { kind: 'date',   field: 'move_in_date', order: 1 },
  occupants:{ kind: 'number', field: 'occupants', min: 1, max: 30, order: 2 },
  pets:     { kind: 'bool',   field: 'has_pets', order: 3 },
  children: { kind: 'bool',   field: 'has_children', order: 4 },
  price:    { kind: 'number', field: 'price_max', min: 1, max: 100000000, order: 5, editor: true },
  location: { kind: 'codes',  field: 'district_codes', order: 6, editor: true },
};

/**
 * The control that closes a client-side gap, or null when the panel has nothing honest to
 * offer. `predicate` is the restriction rule's own {axis:[values]} — supplied by the caller
 * for `rule:` checks, from the rules it fetched by listing id.
 *
 * For a rule the answerable axes are the ones the PREDICATE NAMES: those are the facts the
 * owner's condition turns on, so they are exactly the facts whose absence made the verdict
 * unknown. Nothing is inferred beyond the predicate's own keys.
 */
export function clientFillFor(check, { predicate = null } = {}) {
  if (!check) return null;
  if (ruleIdOf(check.key)) {
    if (!predicate) return null;
    for (const axis of Object.keys(predicate)) {
      const spec = AXIS_FILL[axis];
      if (spec) return { ...spec, axis };
    }
    return null;
  }
  return CLIENT_FILL[check.key] || null;
}

/** Design order for the client band; unknown keys sort last, then alphabetically. */
export function clientGapOrder(check) {
  const spec = check && CLIENT_FILL[check.key];
  return spec?.order ?? 90;
}
